import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  type Case,
  type DocumentDetail,
  DocumentSubType,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import DocumentListTable from "@/components/DocumentListTable/DocumentListTable";
import SearchInput from "@/components/SearchInput";
import { useDocumentApi } from "@/hooks/useApi";
import { getUnarchivedCaseDocumentListOptions } from "@/lib/query-options";
import type {
  ClientResponse,
  DocumentClient,
  JurisdictionResponse,
} from "@/lib/types";
import { addUploadDate, getSubTypeText } from "@/lib/utils";
import { DropDownFilterList, type DropDownItem } from "../DropDownFilterList";

interface CaseDiscoveryProps {
  id: string;
  jurisdiction: JurisdictionResponse | undefined;
  _case: Case | undefined;
  clients: ClientResponse[] | undefined;
  chooseFile: (e: React.MouseEvent) => void;
  isUploading?: boolean;
}

export default function CaseDiscovery(props: CaseDiscoveryProps) {
  const documentApi = useDocumentApi();
  const [partyFilter, setPartyFilter] = useState<string[]>([]);
  const [docFilter, setDocFilter] = useState<string[]>([]);
  const [clientFilter, setClientFilter] = useState<string[]>([]);
  const [nameFilter, setNameFilter] = useState<string>("");

  function handleClearEvent() {
    setPartyFilter([]);
    setDocFilter([]);
    setClientFilter([]);
  }

  const documentsQuery = useQuery({
    ...getUnarchivedCaseDocumentListOptions(documentApi, props.id),
    select: (data) => {
      return data.map((document) => addUploadDate(document as DocumentDetail));
    },
  });

  function getClientName(
    id: string | undefined | null,
    clients: ClientResponse[] | undefined,
  ) {
    if (!clients || !id) return "";
    return clients.find((c) => c.id === id)?.name ?? "";
  }

  function getFilteredDocs(
    documents: DocumentClient[] | undefined,
    nameFilter: string | undefined,
    partyFilter: string[] | undefined,
    docFilter: string[] | undefined,
    clientFilter: string[] | undefined,
  ) {
    if (!documents) {
      return documents;
    }
    let filtered = documents;
    if (nameFilter && nameFilter !== "") {
      filtered = filtered.filter(
        (d) =>
          d.name
            ?.toLocaleLowerCase()
            .includes(nameFilter.toLocaleLowerCase()) ||
          d.info?.propoundingPartyName?.includes(nameFilter) ||
          d.info?.respondingPartyName?.includes(nameFilter),
      );
    }
    if (partyFilter?.length) {
      filtered = filtered.filter(
        (d) =>
          partyFilter.includes(d.info?.propoundingPartyName ?? "") ||
          partyFilter.includes(d.info?.respondingPartyName ?? ""),
      );
    }
    if (docFilter?.length) {
      filtered = filtered.filter((d) =>
        docFilter.includes(
          getSubTypeText(
            d.info?.documentSubType ?? DocumentSubType.Unknown,
            props._case?.jurisdiction ?? "",
          ).short,
        ),
      );
    }
    if (clientFilter?.length) {
      filtered = filtered.filter((d) =>
        clientFilter.includes(getClientName(d.clientId, props.clients)),
      );
    }
    return filtered;
  }

  function getFilters() {
    function getDisplay(active: string[], df: string) {
      if (!active.length) {
        return df;
      }
      let ret = `${active[0]}`;

      if (active.length > 1) {
        ret += ` (+${active.length - 1})`;
      }
      return ret;
    }

    function getUniqueItems(
      documents: DocumentClient[] | undefined | null,
      getItemName: (doc: DocumentClient) => string,
    ): DropDownItem[] {
      return (
        documents
          ?.flatMap((d) => ({ name: getItemName(d), id: getItemName(d) }))
          .filter((p) => p.name !== "")
          .reduce<DropDownItem[]>((acc, current) => {
            if (!acc.some((item) => item.name === current.name)) {
              acc.push(current);
            }
            return acc;
          }, []) ?? []
      );
    }

    let filteredDocs = getFilteredDocs(
      documentsQuery.data ?? [],
      nameFilter,
      undefined,
      docFilter,
      clientFilter,
    );
    const respondingPartyNames = getUniqueItems(
      filteredDocs,
      (d) => d.info?.respondingPartyName ?? "",
    );
    const propoundingPartyNames = getUniqueItems(
      filteredDocs,
      (d) => d.info?.propoundingPartyName ?? "",
    );

    let allParties = [...respondingPartyNames, ...propoundingPartyNames];
    allParties =
      allParties.reduce<DropDownItem[]>((acc, current) => {
        if (!acc.some((item) => item.name === current.name)) {
          acc.push(current);
        }
        return acc;
      }, []) ?? [];

    filteredDocs = getFilteredDocs(
      documentsQuery.data ?? [],
      nameFilter,
      partyFilter,
      undefined,
      clientFilter,
    );
    const docTypes = getUniqueItems(
      filteredDocs,
      (d) =>
        getSubTypeText(
          d.info?.documentSubType ?? DocumentSubType.Unknown,
          props._case?.jurisdiction ?? "",
        ).short,
    );

    filteredDocs = getFilteredDocs(
      documentsQuery.data ?? [],
      nameFilter,
      partyFilter,
      docFilter,
      undefined,
    );
    const clientNames = getUniqueItems(filteredDocs, (d) =>
      getClientName(d.clientId, props.clients),
    );

    return (
      <React.Fragment>
        <span style={{ paddingRight: "4px" }}>Filter By</span>
        {!!partyFilter.length && (
          <label htmlFor="party-filter" className="filter-label">
            Party
          </label>
        )}
        <DropDownFilterList
          key="party-filter"
          text={getDisplay(partyFilter, "Party")}
          itemsList={allParties}
          activeFilter={partyFilter}
          updateFilter={setPartyFilter}
        />
        {!!docFilter.length && (
          <label htmlFor="doc-type-filter" className="filter-label">
            Doc Type
          </label>
        )}
        <DropDownFilterList
          key="documentType-filter"
          text={getDisplay(docFilter, "Doc Type")}
          itemsList={docTypes}
          activeFilter={docFilter}
          updateFilter={setDocFilter}
        />
        {!!clientFilter.length && (
          <label htmlFor="client-filter" className="filter-label">
            Client
          </label>
        )}
        <DropDownFilterList
          id="client-filter"
          key="client-filter"
          text={getDisplay(clientFilter, "Client")}
          itemsList={clientNames}
          activeFilter={clientFilter}
          updateFilter={setClientFilter}
        />
        {!!(partyFilter.length || docFilter.length || clientFilter.length) && (
          <Button
            className="clear-btn"
            variant="link"
            onClick={handleClearEvent}
          >
            Clear All
          </Button>
        )}
      </React.Fragment>
    );
  }
  return (
    <>
      <Row style={{ marginTop: "16px" }} className="align-items-center">
        <Col key="search-actions" className="col-12 col-md-3">
          <SearchInput
            value={nameFilter}
            onChange={setNameFilter}
            placeholder="Search name"
            size="sm"
          />
        </Col>
        <Col key="filters-actions" className="filters col-auto">
          {getFilters()}
        </Col>
      </Row>

      <Row className="mb-3">
        <DocumentListTable
          chooseFile={
            props.chooseFile
              ? (e: React.MouseEvent) => props.chooseFile(e)
              : () => {}
          }
          caseId={props.id}
          nameFilter={nameFilter}
          partyFilter={partyFilter}
          docFilter={docFilter}
          clientFilter={clientFilter}
        />
      </Row>

      <Row>
        <Col className="d-flex justify-content-end">
          <p className="small pb-2">
            <Link
              data-testid="view-archived-case-documents"
              to={`/cases/${props.id}/archive`}
            >
              View Archived Case Documents
            </Link>
          </p>
        </Col>
      </Row>
    </>
  );
}
